import {
    type ApolloQueryResult,
    type QueryHookOptions,
    type TypedDocumentNode,
    useQuery,
} from '@apollo/client';
import { type Maybe } from 'graphql/jsutils/Maybe';
import { useMemo } from 'react';

type NodeOf<E> = E extends { node: infer N } ? N : never;
export type NodesOf<C> = C extends { edges: Maybe<(infer E)[]> }
    ? NonNullable<NodeOf<NonNullable<E>>>[]
    : never;

interface EdgeLike {
    node?: unknown;
}

interface ConnectionLike {
    edges?: Maybe<Maybe<EdgeLike>[]>;
}

function extractNodes(conn: Maybe<ConnectionLike>): unknown[] {
    return (conn?.edges ?? []).map((edge) => edge?.node).filter(Boolean);
}

// The overload signature carries the precise `NodesOf<C>` contract for
// callers; the implementation only has to read the runtime connection shape.
export function toNodes<C>(conn: Maybe<C>): NodesOf<C>;
export function toNodes(conn: Maybe<ConnectionLike>): unknown[] {
    return extractNodes(conn);
}

/**
 * Appends the nodes of `conn` to `target`. The element type is derived from
 * the connection type, so callers keep a precisely typed accumulator.
 */
export function appendNodes<C>(
    target: NodesOf<C>[number][],
    conn: Maybe<C>,
): void;
export function appendNodes(
    target: unknown[],
    conn: Maybe<ConnectionLike>,
): void {
    target.push(...extractNodes(conn));
}

interface ConnectionHook<Payload, Response> {
    nodes: NodesOf<NonNullable<Response>[keyof Response]>;
    loading: boolean;
    refetch: (
        params?: Partial<Payload>,
    ) => Promise<ApolloQueryResult<Response>>;
    raw: Maybe<Response>;
}

export function useQueryConnection<
    Payload extends Record<string, any>,
    Response,
>(
    query: TypedDocumentNode<Response, Payload>,
    variables?: Payload,
    options: QueryHookOptions<Response, Payload> = {},
): ConnectionHook<Payload, Response> {
    const { data, loading, refetch } = useQuery(query, {
        variables,
        ...options,
    });

    // take the first root key
    const key = data ? (Object.keys(data)[0] as keyof Response) : null;
    const conn = key ? data?.[key] : null;

    const nodes = useMemo(() => toNodes(conn), [conn]);
    return { nodes, loading, refetch, raw: data };
}
